<template>
  <div
    ref="chartDomRef"
    class="chart distance-polyline"
  ></div>
</template>

<script setup>
import { DistancePolylineChart } from './chart';
import useDistancePolylineChart from '../useDistancePolylineChart';
import useFrameSelection from '../useFrameSelection';

import { xAxisKeyMap } from '@client/store/activityDetailStore';
import { ref, watch, nextTick } from 'vue';
import useXAxisConfig from '../useXAxisConfig';
import useDrawChart from '../useDrawChart';
import { useActiveAttributes } from '../useActiveAttributes';
import { useListenResize } from '@client/hooks/useBoardResizeEvent';
import { onBeforeUnmount } from 'vue';
import { initStore } from '@client/store/initStore';

const chartDataType = 'overview';
const xAxisKey = xAxisKeyMap['distance-polyline'];

const initStoreRef = initStore();
const THEME = initStoreRef.theme;

const chartRef = ref(null);
const chartDomRef = ref(null);

const { listenFrameSelection, createSelectionZone } = useFrameSelection({
  xAxisKey,
  chartDataType,
  chartRef
});

const { pauseList, tryToDrawChart, chartData } = useDrawChart({ isOverview: true, drawChart });

const { chartAttr, activeAttributes } = useActiveAttributes({ isOverview: true });
watch(activeAttributes, () => {
  nextTick(() => {
    tryToDrawChart();
  });
});
const { cancelListner } = useListenResize(() => {
  tryToDrawChart();
});
onBeforeUnmount(cancelListner);
const { attributes } = useDistancePolylineChart({ activeAttributes, chartAttr });

const { xAxisConfig } = useXAxisConfig({ isPortion: false });

// 绘制图表
function drawChart() {
  chartRef.value?.remove();

  if (!chartDomRef.value) {
    return;
  }
  chartRef.value = new DistancePolylineChart({
    el: chartDomRef.value,
    theme: THEME,
    margin: {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0
    },
    // x轴使用的属性字段名
    xAxisKey,
    // 不显示属性窗口
    showAttrTooltip: false,
    xTickLineSize: 0,
    xTickFontSize: 0,
    // 不绘制x轴刻度线
    showXShortTicks: false,
    // 不绘制x轴刻度文字
    showXTickTexts: false,
    // 不绘制x轴等分线
    showXUniformityLine: false,
    // 不绘制y轴线
    showYLine: false,
    // 不绘制y轴刻度线
    showYTicks: false,
    // 不绘制y轴刻度的文字
    showYTickTexts: false
  });

  const dataList = chartData.value;
  const startX = xAxisConfig.value.startX;
  const endX = xAxisConfig.value.endX;
  const xTicks = xAxisConfig.value.xTicks;

  nextTick(() => {
    listenFrameSelection(dataList);
  });

  // 注入数据
  chartRef.value.setData(dataList);
  // 创建x轴比例尺
  chartRef.value.createXAxis([startX, endX]);
  // 绘制x轴
  chartRef.value.drawXScale(xTicks);
  // 设置显示的属性
  chartRef.value.setShowingAttributes(attributes.value);
  // 生成暂停虚线
  chartRef.value.drawPauseLine(pauseList.value);
  // 绘制框选区遮罩
  createSelectionZone();
}
</script>

<style lang="scss" scoped>
.chart {
  width: 100%;
  height: 100%;
}
</style>
